import React, { useEffect, useRef, useState } from 'react';
import { Button, Slideout } from '@makemydeal/dr-ui-bricks-components';
import { PrinterIcon } from '@interstate/components/Icons';
import { Spinner } from '@interstate/components/Spinner';
import type { FuelSavingsProps, VehicleTab, TradeSectionData } from '../types';
import { CenteredLoader } from './FuelSavingAnalysis.styles';
import { useFuelSavings } from '../hooks';
import { FuelSavingForm } from './FuelSavingForm';
import { FuelSavingsResult } from './FuelSavingsResult';
import {
    FooterButtonGroup,
    FooterContainer,
    FuelSavingContent,
    NoScrollGlobalStyle,
    SlideoutContentWrapper,
    TabButton,
    TabsContainer,
    TradeChevron,
    TradeDivider,
    TradeHeader,
    TradeHeaderButton,
    TradePanel,
    TradePanelsContainer,
    ResultDivider,
    TradeSection,
    ApplyPrintButtonContent
} from './FuelSavingAnalysis.styles';

const EMPTY_VEHICLE_TABS: VehicleTab[] = [];
const EMPTY_TRADES: TradeSectionData[] = [];

export const FuelSavingAnalysis: React.FC<FuelSavingsProps> = ({
    onApply,
    onApplyAndPrint,
    onCancel,
    onValuesChange,
    isFuelSavingCardState = true,
    isFuelSavingSaving = false,
    isFuelSavingSaveSuccess = false,
    vehicleTabs = EMPTY_VEHICLE_TABS,
    isVehicleTabsLoading = false,
    trades = EMPTY_TRADES,
    fuelEconomyData,
    baggageFuelEconomy
}) => {
    const hasSentInitialValues = useRef(false);
    const wasSavingRef = useRef(false);
    const [pendingAction, setPendingAction] = useState<'apply' | 'applyPrint' | null>(null);

    const {
        safeVehicleTabs,
        activeVehicleTabId,
        tradeViewModels,
        buildApplyPayload,
        hasValidResults,
        setActiveVehicleId,
        toggleTradeSection,
        updateTradeInput
    } = useFuelSavings({ isFuelSavingCardState, vehicleTabs, trades, fuelEconomyData, baggageFuelEconomy });

    // Reset the skip-flag when the slideout closes so the next open starts fresh.
    useEffect(() => {
        if (!isFuelSavingCardState) {
            hasSentInitialValues.current = false;
        }
    }, [isFuelSavingCardState]);

    // Persist values to Redux whenever form values change (buildApplyPayload changes).
    // Skip the first run: that fires immediately on mount/open before any user edit.
    // Also skip while vehicle tabs are still loading (scenarios not yet received) to
    // avoid persisting an incomplete payload that would wipe fuelPricePerGallon from baggage.
    useEffect(() => {
        if (!isFuelSavingCardState || !onValuesChange || isVehicleTabsLoading) return;

        if (!hasSentInitialValues.current) {
            hasSentInitialValues.current = true;
            return;
        }

        onValuesChange(buildApplyPayload());
    }, [buildApplyPayload, onValuesChange, isVehicleTabsLoading]);

    const handleCancel = () => {
        setPendingAction(null);
        onCancel?.();
    };

    const handleApplyAndPrint = () => {
        setPendingAction('applyPrint');
        onApplyAndPrint?.(buildApplyPayload());
    };

    const handleApply = () => {
        setPendingAction('apply');
        onApply?.(buildApplyPayload());
    };

    useEffect(() => {
        if (!isFuelSavingCardState) {
            wasSavingRef.current = false;
            return;
        }

        const didFinishSaving = wasSavingRef.current && !isFuelSavingSaving;
        if (didFinishSaving) {
            setPendingAction(null);
            if (isFuelSavingSaveSuccess) {
                onCancel?.();
            }
        }

        wasSavingRef.current = isFuelSavingSaving;
    }, [isFuelSavingCardState, isFuelSavingSaving, isFuelSavingSaveSuccess, onCancel]);

    const showApplyLoading = isFuelSavingSaving && pendingAction === 'apply';
    const showApplyPrintLoading = isFuelSavingSaving && pendingAction === 'applyPrint';

    return (
        <>
            {isFuelSavingCardState && <NoScrollGlobalStyle />}
            <Slideout
                title="Fuel Savings Analysis"
                show={isFuelSavingCardState}
                position="right"
                panelSize={800}
                onClose={handleCancel}
            >
                <SlideoutContentWrapper>
                    {isVehicleTabsLoading ? (
                        <CenteredLoader data-testid="vehicle-tabs-loading-spinner">
                            <Spinner />
                        </CenteredLoader>
                    ) : (
                        <FuelSavingContent>
                            <TabsContainer>
                                {safeVehicleTabs.map((vehicle) => (
                                    <TabButton
                                        key={vehicle.id}
                                        type="button"
                                        data-testid={`vehicle-tab-${vehicle.id}`}
                                        $isActive={activeVehicleTabId === vehicle.id}
                                        onClick={() => setActiveVehicleId(vehicle.id)}
                                    >
                                        {vehicle.label}
                                    </TabButton>
                                ))}
                            </TabsContainer>

                            <TradePanelsContainer>
                                {tradeViewModels.map(({ trade, index, values, isExpanded, showResults, savings }) => {
                                    return (
                                        <TradeSection key={trade.id}>
                                            <TradeHeaderButton
                                                type="button"
                                                data-testid={`trade-toggle-${trade.id}`}
                                                onClick={() => toggleTradeSection(trade.id)}
                                            >
                                                <TradeChevron $isExpanded={isExpanded} aria-hidden="true" />
                                                <TradeHeader>
                                                    Trade {index + 1}: {trade.name}
                                                </TradeHeader>
                                            </TradeHeaderButton>

                                            {isExpanded && (
                                                <TradePanel>
                                                    <FuelSavingForm
                                                        values={values}
                                                        onFieldChange={(field, value) => updateTradeInput(trade.id, field, value)}
                                                        onMilesPerChange={(value) => updateTradeInput(trade.id, 'milesPer', value)}
                                                    />
                                                    {showResults && savings && (
                                                        <>
                                                            <ResultDivider />
                                                            <FuelSavingsResult
                                                                monthly={savings.monthly}
                                                                yearly={savings.yearly}
                                                                threeYears={savings.threeYears}
                                                            />
                                                        </>
                                                    )}
                                                </TradePanel>
                                            )}

                                            <TradeDivider />
                                        </TradeSection>
                                    );
                                })}
                            </TradePanelsContainer>
                        </FuelSavingContent>
                    )}

                    <FooterContainer>
                        <FooterButtonGroup>
                            <Button
                                buttonStyle="secondary"
                                onClick={handleCancel}
                                className="cancel-button"
                                data-testid="fuel-saving-cancel-btn"
                            >
                                Cancel
                            </Button>
                            <Button
                                buttonStyle="secondary"
                                onClick={handleApplyAndPrint}
                                className="apply-print-button"
                                data-testid="fuel-saving-apply-print-btn"
                                disabled={!hasValidResults || isFuelSavingSaving}
                            >
                                <ApplyPrintButtonContent>
                                    {showApplyPrintLoading && (
                                        <Spinner size={18} data-testid="fuel-saving-apply-print-loading-spinner" />
                                    )}
                                    Apply and Print <PrinterIcon fontSize={16} />
                                </ApplyPrintButtonContent>
                            </Button>
                            <Button
                                buttonStyle="primary"
                                onClick={handleApply}
                                className="apply-button"
                                data-testid="fuel-saving-apply-btn"
                                disabled={!hasValidResults || isFuelSavingSaving}
                            >
                                <ApplyPrintButtonContent>
                                    {showApplyLoading && <Spinner size={18} data-testid="fuel-saving-apply-loading-spinner" />}
                                    Apply
                                </ApplyPrintButtonContent>
                            </Button>
                        </FooterButtonGroup>
                    </FooterContainer>
                </SlideoutContentWrapper>
            </Slideout>
        </>
    );
};
